import { defineStore, storeToRefs } from 'pinia';
import { ref, computed } from 'vue';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppThirdPartyPlugins } from '@/stores/app/thirdParty';
import { recruitInitNavbar } from '@/api/recruit';
import { getEnvironment } from '@/helpers/environment';
import { canAccessOrganizationSettings } from '@/helpers/organizationSettingsTabs';

export const useAppPlatform = defineStore('appPlatform', () => {
  const { addErrorSnackbar } = useAppSnackbar();

  const authDataStore = useAppAuthData();
  const { integrated } = storeToRefs(authDataStore);
  const { checkRbacPermissions, checkOneOfPermissions } = authDataStore;

  const platform = ref({});
  const currentJobId = ref('');
  const platformInitialized = ref(false);

  // ========================= SideNav ==============================
  // ================================================================

  const homeNavLinks = computed(() => {
    if (!checkOneOfPermissions(['viewPostSummary', 'viewAdminPostSummary'])) {
      return {};
    }

    return {
      name: 'Overview',
      id: 'overview',
      icon: '$squares-four',
      iconClass: 'set-fill-color',
      sections: [
        {
          title: 'Home',
          to: 'home',
          id: 'overview-home',
        },
      ],
    };
  });

  const jobPostsNavLinks = computed(() => {
    if ( // Check basic features for showing posts category at all
      !checkOneOfPermissions(['fetchLibraryPublished'])
    ) return {};

    const sections = [];

    // Libraries - Posts
    if (checkOneOfPermissions([ // Minimal permissions
      'fetchLibraryPublished',
      'fetchLibraryDrafts',
    ])) {
      if (checkRbacPermissions(['createJob'])) {
        sections.push(
          {
            title: 'New Post',
            to: 'create-job-post',
            id: 'job-posts-new-post',
          },
        );
      }
      // { // Templates as nav item to be addressed later
      //   title: 'New Post Template',
      //   show: checkRbacPermissions(['editJobTemplates']),
      //   to: '',
      //   id: 'job-posts-new-post-template',
      // },

      const libraryPostsSection = {
        title: 'Libraries',
        id: 'job-posts-libraries',
        links: [],
      };

      if (checkRbacPermissions(['fetchLibraryDrafts'])) {
        libraryPostsSection.links.push({
          to: 'draft',
          title: 'Draft',
        });
      }
      if (checkRbacPermissions(['fetchLibraryPublished'])) {
        libraryPostsSection.links.push({
          to: 'published',
          title: 'Published',
        });
      }
      if (checkRbacPermissions(['fetchLibraryUnpublished'])) {
        libraryPostsSection.links.push({
          to: 'unpublished',
          title: 'Unpublished',
        });
      }
      if (checkRbacPermissions(['fetchLibraryFlagship'])) {
        libraryPostsSection.links.push({
          to: 'flagship',
          title: 'Flagship',
          isNew: true,
        });
      }
      if (checkRbacPermissions(['viewTemplates'])) {
        libraryPostsSection.links.push({
          to: 'templates',
          title: 'Template',
        });
      }

      sections.push(libraryPostsSection);
    }

    // Workflows - Posts
    if (checkOneOfPermissions([
      'viewComplianceReport',
      'viewEmployerBrandReport',
      'viewJobPerformanceReport',
    ])) {
      const workflowPostsSection = {
        title: 'Workflows',
        id: 'job-posts-workflows',
        links: [],
      };

      if (checkRbacPermissions(['viewComplianceReport'])) {
        workflowPostsSection.links.push({
          to: 'compliance-report',
          title: 'Compliance',
        });
      }
      if (checkRbacPermissions(['viewEmployerBrandReport'])) {
        workflowPostsSection.links.push({
          to: 'employer-brand-report',
          title: 'Employer Brand',
        });
      }
      if (checkRbacPermissions(['viewJobPerformanceReport'])) {
        workflowPostsSection.links.push({
          to: 'job-performance-report',
          title: 'Job Performance',
        });
      }

      sections.push(workflowPostsSection);
    }

    // Insights - Posts
    if (checkOneOfPermissions([
      'viewContentReport',
      'viewHealthReport',
      'viewRecruitingPerformanceReport',
    ])) {
      const insightsPostsSection = {
        title: 'Insights',
        id: 'job-posts-insights',
        links: [],
      };

      if (checkRbacPermissions(['viewContentReport'])) {
        insightsPostsSection.links.push({
          to: 'content-snapshot',
          title: 'Content Snapshot',
        });
      }
      if (checkRbacPermissions(['viewHealthReport'])) {
        insightsPostsSection.links.push({
          to: 'weekly-snapshot',
          title: 'Weekly Snapshot',
        });
      }
      if (checkRbacPermissions(['viewRecruitingPerformanceReport'])) {
        insightsPostsSection.links.push({
          to: 'recruiting-performance',
          title: 'Recruiting Performance',
        });
      }

      sections.push(insightsPostsSection);
    }

    return {
      name: 'Job Posts',
      id: 'job-posts',
      icon: '$megaphone',
      iconClass: 'set-stroke-color',
      sections,
    };
  });

  const jobProfilesNavLinks = computed(() => {
    if (!checkOneOfPermissions([
      'listDraftProfileDocuments',
      'listJobProfileLibraryDocuments',
      'viewJobProfileArchitecture',
      'listEmployeeJobProfileAcknowledgements',
    ])) return {};

    const sections = [];

    if (checkRbacPermissions(['createDraftProfile'])) {
      sections.push(
        {
          title: 'New Profile',
          to: 'create-job-profile',
          id: 'job-profiles-new-profile',
        },
      );
    }

    // if (checkRbacPermissions(['createProfileTemplate'])) {
    //   sections.push(
    //     {
    //       title: 'New Profile Template',
    //       to: '',
    //       show: true,
    //       id: 'job-profiles-new-profile-template',
    //     },
    //   );
    // }

    if (checkOneOfPermissions([
      'searchJobProfileLibrary',
      'listJobProfileLibraryDocuments',
    ])) {
      const libraryProfilesSection = {
        title: 'Libraries',
        id: 'job-profiles-libraries',
        links: [],
      };

      if (checkRbacPermissions(['searchJobProfileLibrary'])) {
        libraryProfilesSection.links.push({
          to: 'draft-job-profiles',
          title: 'Draft',
        });
      }
      if (checkRbacPermissions(['listJobProfileLibraryDocuments'])) {
        libraryProfilesSection.links.push({
          to: 'hris-job-profiles',
          title: 'HRIS-Linked',
        });
      }
      if (checkRbacPermissions(['listJobProfileTemplates'])) {
        libraryProfilesSection.links.push({
          to: 'job-profile-templates',
          title: 'Template',
        });
      }

      sections.push(libraryProfilesSection);
    }

    if (checkOneOfPermissions([
      'listEmployeeJobProfileAcknowledgements',
      'listFlsaSurveyReportProfiles',
      'viewApprovalsDashboard',
      'listApprovalTemplates',
    ])) {
      const workflowProfileSection = {
        title: 'Workflows',
        id: 'job-profiles-workflows',
        links: [],
      };
      if (checkRbacPermissions(['listApprovalTemplates'])) {
        workflowProfileSection.links.push({
          title: 'Approval Templates',
          to: 'approval-template-editor',
        });
      }
      if (checkRbacPermissions(['listEmployeeJobProfileAcknowledgements'])) {
        workflowProfileSection.links.push({
          title: 'Acknowledgement',
          to: 'employee-acknowledgements',
        });
      }
      if (checkRbacPermissions(['viewApprovalsDashboard'])) {
        workflowProfileSection.links.push({
          title: 'Approvals Dashboard',
          to: 'approvals-library',
        });
      }
      if (checkRbacPermissions(['listFlsaSurveyReportProfiles'])) {
        workflowProfileSection.links.push({
          title: 'Federal FLSA',
          to: 'federal-flsa-dashboard',
        });
      }

      sections.push(workflowProfileSection);
    }

    if (checkRbacPermissions(['viewJobProfileArchitecture'])) {
      sections.push(
        {
          title: 'Insights',
          id: 'job-profiles-insights',
          links: [
            {
              title: 'Architecture',
              to: 'job-architecture',
            },
          ],
        },
      );
    }

    return {
      name: 'Job Profiles',
      icon: '$tree-view',
      iconClass: 'set-fill-color',
      id: 'job-profiles',
      sections,
    };
  });

  const jobRequisitionsNavLinks = computed(() => {
    if ( // check basic features for showing requisitions at all
      !checkRbacPermissions(['viewRequisitionDocuments'])
    ) return {};

    const sections = [];

    if (checkRbacPermissions(['viewRequisitionDocuments'])) {
      sections.push(
        {
          title: 'Libraries',
          id: 'job-requisitions-libraries',
          links: [
            {
              to: 'draft-requisitions',
              title: 'Draft',
            },
            {
              to: 'open-requisitions',
              title: 'Open',
            },
            {
              to: 'closed-requisitions',
              title: 'Closed',
            },
          ],
        },
      );
    }

    if (checkOneOfPermissions([
      'viewSourceInsightsFilters',
      'viewProcessInsightsFilters',
      'viewEeoFilters',
    ])) {
      const insightsRequisitionsSections = {
        title: 'Insights',
        id: 'job-requisitions-insights',
        links: [],
      };

      if (checkRbacPermissions(['viewProcessInsightsExecutive'])) {
        insightsRequisitionsSections.links.push({
          to: 'executive-insights',
          title: 'Executive',
        });
      }
      if (checkRbacPermissions(['viewProcessInsightsPassthrough'])) {
        insightsRequisitionsSections.links.push({
          to: 'pass-through',
          title: 'Pass-through',
        });
      }
      if (checkRbacPermissions(['viewProcessInsightsDropoff'])) {
        insightsRequisitionsSections.links.push({
          to: 'drop-off',
          title: 'Drop-off',
        });
      }
      if (checkRbacPermissions(['viewProcessInsightsGender'])) {
        insightsRequisitionsSections.links.push({
          to: 'gender-report',
          title: 'Gender',
        });
      }
      if (useAppAuthData().isDemographicSurveyReportVisible) {
        insightsRequisitionsSections.links.push({
          to: 'demographic-survey-report',
          title: 'Demographic Survey',
        });
      }
      if (checkRbacPermissions(['viewProcessInsightsHiring'])) {
        insightsRequisitionsSections.links.push({
          to: 'hiring-report',
          title: 'Hiring',
        });
      }
      if (checkRbacPermissions(['viewSourceInsightsOverall'])) {
        insightsRequisitionsSections.links.push({
          to: 'overall-report',
          title: 'Overall',
        });
      }
      if (checkRbacPermissions(['viewSourceInsightsInbound'])) {
        insightsRequisitionsSections.links.push({
          to: 'inbound-report',
          title: 'Inbound',
        });
      }
      if (checkRbacPermissions(['viewSourceInsightsOutbound'])) {
        insightsRequisitionsSections.links.push({
          to: 'outbound-report',
          title: 'Outbound',
        });
      }

      sections.push(insightsRequisitionsSections);
    }

    return {
      name: 'Job Requisitions',
      icon: '$fact-check-line',
      iconClass: 'set-stroke-color',
      id: 'job-requisitions',
      sections,
    };
  });

  const usageNavLinks = computed(() => {
    if (!checkRbacPermissions(['viewUsageReport'])) return {};

    const sections = [];

    sections.push(
      {
        title: 'Posts',
        to: 'usage',
        id: 'usage-posts',
      },
    );

    return {
      name: 'Usage',
      icon: '$leaderboard',
      iconClass: 'set-stroke-color',
      id: 'usage',
      sections,
    };
  });

  // Gather all the categories and filter out the empty ones
  const allNavItems = computed(() => ([
    homeNavLinks.value,
    jobProfilesNavLinks.value,
    jobPostsNavLinks.value,
    jobRequisitionsNavLinks.value,
    usageNavLinks.value,
  ]).filter((item) => item.name && item.sections?.length));

  /* ============= New Account dropdown ========== */
  const settingsNavLinks = computed(() => {
    const {
      showIntegrationsPage,
      atsName,
    } = platform.value;

    const sections = [];

    sections.push({
      name: 'User',
      links: [
        {
          to: 'account',
          title: 'My Account',
        },
        {
          to: 'administrators',
          title: 'My Team',
        },
      ],
    });

    const organizationLinks = [];

    if (showIntegrationsPage && (!integrated.value || checkRbacPermissions(['manageIntegrations']))) {
      organizationLinks.push({
        to: 'ats-integration',
        title: `Connect ${atsName}`,
      });
    }

    if (checkRbacPermissions(['manageTeam'])) {
      organizationLinks.push({
        to: 'style-guide',
        title: 'Style Guide',
      });
      organizationLinks.push({
        to: 'policy-guide',
        title: 'Policy Guide',
      });
    }

    if (canAccessOrganizationSettings(authDataStore)) {
      organizationLinks.push({
        to: 'organization-settings',
        title: 'Settings',
      });
    }

    if (organizationLinks.length > 0) {
      sections.push({
        name: 'Organization',
        links: organizationLinks,
      });
    }

    const connectSection = {
      name: 'Connect',
      links: [],
    };

    connectSection.links.push({
      href: 'https://chromewebstore.google.com/detail/'
            + 'jobnav-anywhere/hhnemhalmbhdldblamkefkjnalpmadhm?hl=en&authuser=0',
      title: 'JobNav Anywhere',
      postIcon: '$arrow-up-right',
    });

    connectSection.links.push({
      title: 'What\'s New',
      href: '#',
      attributes: {
        'data-beacon-article-sidebar': '67351309734eb350c5fdb09b',
      },
      postIcon: '$arrow-up-right',
    });
    connectSection.links.push({
      title: 'Help',
      href: useAppThirdPartyPlugins().helpscoutSiteUrl,
      attributes: {
        target: '_blank',
        rel: 'noopener noreferrer',
      },
      postIcon: '$arrow-up-right',
    });

    sections.push(connectSection);

    sections.push({
      name: 'Site',
      links: [
        {
          href: '/logout',
          title: 'Log Out',
          attributes: {
            // This data test ID needs to be here for Canary scripts to work
            'data-testid': 'logout-link',
          },
        },
      ],
    });

    return sections;
  });

  const superuserNavLinks = computed(() => {
    const {
      userId,
      projectId,
      hasProject,
      inviteKey,
    } = platform.value;

    const isProductionEnv = ['production', 'test-app'].includes(getEnvironment());

    if (!checkRbacPermissions(['adminBrowsePanel'])) return [];

    const sections = [];

    const datapeopleSection = {
      name: 'JobNav',
      links: [],
    };

    datapeopleSection.links.push({
      href: '/admin/project',
      title: 'Admin Console',
    });
    if (userId) {
      datapeopleSection.links.push({
        href: `/admin/user/update/${userId}`,
        title: 'User Settings',
      });
    }
    if (projectId && checkRbacPermissions(['adminProjectSuperuser'])) {
      datapeopleSection.links.push({
        href: `/admin/project/update/${projectId}`,
        title: 'Project Settings',
      });
    }
    if (projectId
      && (
        checkRbacPermissions(['adminBrowsePanel'])
        && !checkRbacPermissions(['adminProjectSuperuser'])
      )) {
      datapeopleSection.links.push({
        href: `/admin/project/view/${projectId}`,
        title: 'Project Settings',
      });
    }
    if (hasProject) {
      datapeopleSection.links.push({
        to: 'content-snapshot',
        title: 'Public Content Report',
        inviteKey,
      }, {
        to: 'language-snapshot',
        title: 'Public Language Report',
        inviteKey,
      });
    }
    if (currentJobId.value) {
      datapeopleSection.links.push({
        href: `/admin/job?JobSearch[id]=${currentJobId.value}`,
        title: 'Job Admin',
      });
    }

    sections.push(datapeopleSection);

    const switchMeToSection = {
      name: 'Switch me to...',
      links: [],
    };

    switchMeToSection.links.push(
      {
        href: '/admin/project/switch?project_name=JobNav Demo',
        title: 'JobNav Demo',
      },
      {
        href: '/admin/project/switch?project_name=Recruiter Demo',
        title: 'Recruiter Demo',
      },
      {
        href: '/admin/project/switch?project_name=Job Architecture Demo',
        title: 'Job Architecture Demo',
      },
      {
        href: '/admin/project/switch?project_name=Greenhouse Demo',
        title: 'Greenhouse Demo',
      },
      {
        href: '/admin/project/switch?project_name=Workday Demo',
        title: 'Workday Demo',
      },
      {
        href: '/admin/project/switch?project_name=ADP Demo',
        title: 'ADP Demo',
      },
      {
        href: '/admin/project/switch?project_name=SmartRecruiters Demo',
        title: 'SmartRecruiters Demo',
      },
      {
        href: '/admin/project/switch?project_name=Lever Demo',
        title: 'Lever Demo',
      },
      {
        href: '/admin/project/switch?project_name=Workable Demo',
        title: 'Workable Demo',
      },
      {
        href: '/admin/project/switch?project_name=UKG Pro Demo',
        title: 'UKG Pro Demo',
      },
      {
        href: '/admin/project/switch?project_name=iCIMS Demo',
        title: 'iCIMS Demo',
      },
      {
        href: '/admin/project/switch?project_name=Taleo Business Demo',
        title: 'Taleo Business Demo',
      },
      {
        href: '/admin/project/switch?project_name=Taleo Enterprise Demo',
        title: 'Taleo Enterprise Demo',
      },
    );

    if (!isProductionEnv) {
      switchMeToSection.links.push(
        {
          href: '/admin/project/switch?project_name=Insights Demo',
          title: 'Insights Demo',
        },
        {
          href: '/admin/project/switch?project_name=Greenhouse Non-Integrated',
          title: 'Greenhouse Non-Integrated',
        },
        {
          href: '/admin/project/switch?project_name=Workday Non-Integrated',
          title: 'Workday Non-Integrated',
        },
        {
          href: '/admin/project/switch?project_name=SmartRecruiters Non-Integrated',
          title: 'SmartRecruiters Non-Integrated',
        },
        {
          href: '/admin/project/switch?project_name=Lever Non-Integrated',
          title: 'Lever Non-Integrated',
        },
        {
          href: '/admin/project/switch?project_name=iCIMS Non-Integrated',
          title: 'iCIMS Non-Integrated',
        },
      );
    }

    sections.push(switchMeToSection);

    return sections;
  });

  /* ==============
    actions
  ============== */

  const setPlatformCurrentJobId = (id) => {
    currentJobId.value = id;
  };

  const initPlatform = async (jobId, { setInitFieldsAction }) => {
    try {
      const responseJSON = await recruitInitNavbar({ jobId });

      if (responseJSON.success) {
        const {
          isApplicationReportingEnabled,
          templateList,
          showWeeklyReports,
        } = responseJSON.data || {};

        setInitFieldsAction({
          isApplicationReportingEnabled, templateList, showWeeklyReports,
        });

        // these fields have been moved to editor vuex module
        ['isApplicationReportingEnabled', 'templateList', 'showWeeklyReports'].forEach((field) => {
          delete responseJSON.data[field];
        });

        platform.value = { ...responseJSON.data };
        platformInitialized.value = true;
        setPlatformCurrentJobId(jobId);
      }
    } catch (error) {
      if (error.response?.code === 'project_not_found') {
        addErrorSnackbar({ text: 'Project not found' });
      } else {
        throw error;
      }
    }
  };

  return {
    /* state */
    platform,
    platformInitialized,
    currentJobId,

    // new sideNav
    allNavItems,
    settingsNavLinks,
    superuserNavLinks,

    /* actions */
    initPlatform,
    setPlatformCurrentJobId,
  };
});
